import { Box, CircularProgress, Divider, Tooltip, Typography } from '@mui/material';
import { AccordionSection } from './utils';
import { GameState } from './Game';
import {
  GAME_COVERS_RESOLUTIONS,
  GAME_COVERS_RESOLUTIONS_LIST,
  GameCovers,
  GameCoverWithUploadedFile,
  GameVideo,
} from '../../../../../../common/graphql/games/game';
import { AdminSubmission } from '../gql/submission-graphql';
import { StyledBodyText } from '../../../../../../common/Styleguide/Common/Text';
import config from '../../../../../../common/config';
import InfoIcon from '@mui/icons-material/Info';
import SubmissionUpdateGameCovers from '../../Submission/UpdateSubmission/SubmissionUpdateGameCovers';
import { useState } from 'react';
import { UploadedFile } from '../../../../../../common/domain/upload';
import { StyledButton } from '../../../../../../common/Styleguide/Common/Button';
import SaveIcon from '@mui/icons-material/Save';
import { updateSubmissionArt } from './gql/submission-update-art';
import { synchroniseCover as synchroniseCovers } from './gql/synchronise-cover.mutation';
import VideoUpload from '../VideoUpload';
import SyncIcon from '@mui/icons-material/Sync';
import { synchroniseVideos } from './gql/synchronise-videos.mutation';
import cleanCopy from '../../../../../../common/graphql/clean-copy';
import DeleteIcon from '@mui/icons-material/Delete';
import { COLORS } from '../../../../../../common/Styleguide/Common/colors';
import ReplayIcon from '@mui/icons-material/Replay';
import { SECTIONS, getSectionDefaultExpanded } from './sectionExpandedData';
import { SubmissionVideo } from '@crazygames/graph-types/submission/submission-video.type';
import { AdminSubmissionUpdateArtInput } from '@crazygames/graph-types/submission/admin-submission-update-art-input.type';
import { VideoSyncType } from '@crazygames/graph-types/submission/admin-submission-sync-videos-input.type';

export const PREVIEW_VIDEO_HEIGHT = 180;
export const PREVIEW_VIDEO_WIDTH = 320;

interface Props {
  state: GameState;
  submission: AdminSubmission;
}

/**
 * Renders live game art (covers + videos). These can only be viewed.
 * Renders submission art (covers + videos). These can be updated, and synced to replace live game art.
 */
const GameArtSection: React.FC<Props> = ({ state, submission }: Props) => {
  const [submissionCovers, setSubmissionCovers] = useState<GameCoverWithUploadedFile[]>(submission.gameCovers || []);
  const [submissionVideos, setSubmissionVideos] = useState<SubmissionVideo | null>(submission.videos);
  const [removeLiveVideos, setRemoveLiveVideos] = useState(false); // if this will be sent as true to graph, it will clean game videos
  const [hasChanges, setHasChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const renderLiveGameTitle = () => {
    return (
      <Box display={'flex'} flexDirection="row" alignItems="center">
        <StyledBodyText variant="h3">Live game art</StyledBodyText>
        <Tooltip
          title={
            'Live art is currently displayed on CrazyGames.com, and is stored in the game table in the database. To update it, upload new submission art below, save it, and synchronise it.'
          }
          arrow
        >
          <InfoIcon style={{ height: 16, width: 16, marginLeft: 4 }} />
        </Tooltip>
      </Box>
    );
  };

  const renderLiveGameCovers = () => {
    const covers = JSON.parse(state.originalGame.rawGameCoversUrls) as GameCovers;
    return (
      <Box display={'flex'} flexDirection="row" gap={2}>
        {GAME_COVERS_RESOLUTIONS_LIST.map((resolution) => {
          if (covers[resolution]) {
            return (
              <img
                src={covers[resolution]}
                alt={`${resolution} live cover`}
                style={{ height: PREVIEW_VIDEO_HEIGHT, width: 'auto' }}
                key={resolution}
              />
            );
          } else {
            return <div key={resolution}>Missing {resolution}</div>;
          }
        })}
      </Box>
    );
  };

  const renderLiveGameVideos = () => {
    if (!state.originalGame.rawVideo) {
      return (
        <Typography color="orange" fontWeight={700} sx={{ mt: 2 }}>
          Missing rawVideo
        </Typography>
      );
    }

    if (removeLiveVideos) {
      return (
        <Box>
          <Typography color={COLORS.alert[100]} fontWeight={700} sx={{ mt: 2 }}>
            Live videos will be cleaned when you click "Save art".
          </Typography>
          <StyledButton
            onClick={() => {
              setRemoveLiveVideos(false);
            }}
            variant="contained"
          >
            <ReplayIcon />
            Restore videos
          </StyledButton>
        </Box>
      );
    }

    const videos = JSON.parse(state.originalGame.rawVideo) as GameVideo;
    const { videoCdnPrefix } = config.aws;
    const landscape = videos.sizes.sort((a, b) => b.width - a.width)[0]?.location;
    const portrait = videos.portraitSizes.sort((a, b) => b.width - a.width)[0]?.location;

    return (
      <Box display={'flex'} flexDirection="row" gap={2} sx={{ mt: 2 }}>
        {!landscape && (
          <Typography color="orange" fontWeight={700}>
            Missing landscape video
          </Typography>
        )}
        {landscape && (
          <video autoPlay={true} loop={true} muted={true} style={{ height: PREVIEW_VIDEO_HEIGHT, width: PREVIEW_VIDEO_WIDTH }}>
            <source src={`${videoCdnPrefix}/${landscape}`} type="video/mp4" />
          </video>
        )}
        {!portrait && (
          <Typography color="orange" fontWeight={700}>
            Missing portrait video
          </Typography>
        )}
        {portrait && (
          <video autoPlay={true} loop={true} muted={true} style={{ height: PREVIEW_VIDEO_WIDTH, width: PREVIEW_VIDEO_HEIGHT }}>
            <source src={`${videoCdnPrefix}/${portrait}`} type="video/mp4" />
          </video>
        )}
        <StyledButton
          onClick={() => {
            setRemoveLiveVideos(true);
            setHasChanges(true);
          }}
          variant="contained"
        >
          <DeleteIcon />
          Remove videos
        </StyledButton>
      </Box>
    );
  };

  const renderSubmissionTitle = () => {
    return (
      <Box display={'flex'} flexDirection="row" alignItems="center">
        <StyledBodyText variant="h3">Submission art</StyledBodyText>
        <Tooltip
          title={
            'Submission art is stored in submission table in the database. This is also what the developer sees on his submission page. After uploading new art, you should sync it if you want it to go live on CrazyGames.com.'
          }
          arrow
        >
          <InfoIcon style={{ height: 16, width: 16, marginLeft: 4 }} />
        </Tooltip>
      </Box>
    );
  };

  const renderSubmissionCovers = () => {
    const onChangeGameCovers = (size: GAME_COVERS_RESOLUTIONS, uploadedFile: UploadedFile | null) => {
      const newGameCovers = submissionCovers.filter((gc) => gc.size !== size) || [];
      newGameCovers.push({
        size,
        uploadedFile,
      });
      setSubmissionCovers(newGameCovers);
      setHasChanges(true);
    };

    return (
      <div style={{ width: 700 }}>
        <SubmissionUpdateGameCovers gameCovers={submissionCovers} onChange={onChangeGameCovers} />
      </div>
    );
  };

  const renderSubmissionVideos = () => {
    return (
      <Box display={'flex'} flexDirection="row" gap={2} sx={{ mt: 2 }}>
        <VideoUpload
          originalVideo={submissionVideos?.videoThumbnail ?? null}
          slug={submission.slug}
          mandatory={true}
          onChange={(thumbnailLocation, originalLocation) => {
            setSubmissionVideos({
              videoOriginal: originalLocation,
              videoThumbnail: thumbnailLocation,
              portraitVideoOriginal: submissionVideos?.portraitVideoOriginal ?? null,
              portraitVideoThumbnail: submissionVideos?.portraitVideoThumbnail ?? null,
            });
            setHasChanges(true);
          }}
          label={'Landscape video *'}
          videoContainerSize={{ width: PREVIEW_VIDEO_WIDTH, height: PREVIEW_VIDEO_HEIGHT }}
        />
        <VideoUpload
          originalVideo={submissionVideos?.portraitVideoThumbnail ?? null}
          slug={submission.slug}
          onChange={(thumbnailLocation, originalLocation) => {
            setSubmissionVideos({
              portraitVideoOriginal: originalLocation,
              portraitVideoThumbnail: thumbnailLocation,
              videoOriginal: submissionVideos?.videoOriginal ?? null,
              videoThumbnail: submissionVideos?.videoThumbnail ?? null,
            });
            setHasChanges(true);
          }}
          label={'Portrait video'}
          videoContainerSize={{ width: PREVIEW_VIDEO_HEIGHT, height: PREVIEW_VIDEO_WIDTH }}
        />
      </Box>
    );
  };

  const renderActionButtons = () => {
    if (isLoading) {
      return <CircularProgress disableShrink={true} size={32} variant="indeterminate" />;
    }

    return (
      <Box display={'flex'} flexDirection="row" alignItems={'center'} gap={2} sx={{ mt: 2 }}>
        {hasChanges && (
          <StyledButton variant="contained" onClick={saveSubmissionArt}>
            <SaveIcon /> Save art
          </StyledButton>
        )}
        <StyledButton variant="contained" onClick={onSyncCoverClick}>
          <SyncIcon /> Covers
        </StyledButton>
        {submissionVideos?.videoOriginal && (
          <StyledButton variant="contained" onClick={() => onSyncVideosClick('landscape')}>
            <SyncIcon /> Landscape video
          </StyledButton>
        )}
        {submissionVideos?.portraitVideoOriginal && (
          <StyledButton variant="contained" onClick={() => onSyncVideosClick('portrait')}>
            <SyncIcon /> Portrait video
          </StyledButton>
        )}
        {submissionVideos?.portraitVideoOriginal && submissionVideos?.videoOriginal && (
          <StyledButton variant="contained" onClick={() => onSyncVideosClick('both')}>
            <SyncIcon /> Both videos
          </StyledButton>
        )}
        <Tooltip
          title={
            'Sync covers is instant and you will see live art above updated after clicking it. Sync Videos takes some time as it needs to generate all the correct sizes. This is a background task however, so once you click "Sync Videos" you can close this page.'
          }
          arrow
        >
          <InfoIcon style={{ height: 16, width: 16 }} />
        </Tooltip>
      </Box>
    );
  };

  const saveSubmissionArt = async () => {
    try {
      setIsLoading(true);
      const input: AdminSubmissionUpdateArtInput = {
        id: submission.id,
        gameCovers: submissionCovers.map((gameCover) => ({
          size: gameCover.size,
          uploadedFile: {
            size: gameCover.uploadedFile!.size,
            path: gameCover.uploadedFile!.path,
            uploadId: gameCover.uploadedFile!.uploadId,
          },
        })),
        videos: submissionVideos ? cleanCopy(submissionVideos, []) : null,
        removeLiveVideos: removeLiveVideos,
      };
      await updateSubmissionArt(input, submission.slug);
      setHasChanges(false);
      setRemoveLiveVideos(false);
    } catch (_) {
      throw new Error('Could not update submission art');
    } finally {
      setIsLoading(false);
    }
  };

  const onSyncCoverClick = async () => {
    setIsLoading(true);
    try {
      await synchroniseCovers({ id: submission.id }, submission.slug);
    } catch (_) {
      throw new Error('Could not synchronise submission covers');
    } finally {
      setIsLoading(false);
    }
  };

  const onSyncVideosClick = async (syncType: VideoSyncType) => {
    setIsLoading(true);
    try {
      await synchroniseVideos({ id: submission.id, syncType }, submission.slug);
    } catch (_) {
      throw new Error('Could not synchronise submission videos');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AccordionSection
      title={SECTIONS.art.name}
      id={SECTIONS.art.id}
      defaultExpanded={getSectionDefaultExpanded(SECTIONS.art, submission?.status)}
    >
      {renderLiveGameTitle()}
      {renderLiveGameCovers()}
      {renderLiveGameVideos()}
      <Divider sx={{ mr: -2, ml: -2, mt: 2, mb: 2 }} />
      {renderSubmissionTitle()}
      {renderSubmissionCovers()}
      {renderSubmissionVideos()}
      {renderActionButtons()}
    </AccordionSection>
  );
};

export default GameArtSection;
